import React, { useLayoutEffect, useRef } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import { FinancialAccountCombobox } from "@components/financialAccount/AccountsFilter/FinancialAccountCombobox";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { BaseCellProps } from "./index";

interface FinancialAccountCellProps extends BaseCellProps<number> {
    accountFilterPredicate?: (account: FinancialAccount) => boolean;
}

export const FinancialAccountCell: React.FC<FinancialAccountCellProps> = ({
    value,
    isEditing,
    finishEditing,
    rowIdx,
    colId,
    accountFilterPredicate,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerClassName = `financial-account-cell-trigger-${rowIdx}-${colId}`;
    const financialAccounts = useFinancialAccounts();
    const selectedAccount = financialAccounts.find(
        (account) => account.id === value,
    );
    const display = selectedAccount?.name ? (
        <BreakableText text={selectedAccount.name} showTooltip />
    ) : null;

    useLayoutEffect(() => {
        if (isEditing && containerRef.current) {
            containerRef.current
                .querySelector<HTMLElement>(`.${triggerClassName}`)
                ?.click();
        }
    }, [isEditing, triggerClassName]);

    if (isEditing) {
        return (
            <>
                <div
                    ref={containerRef}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (["Enter", "Escape"].includes(e.key)) {
                            finishEditing();
                        }
                    }}
                >
                    <FinancialAccountCombobox
                        value={value}
                        onValueChange={(accountId) => finishEditing(accountId)}
                        onOpenChange={(open) => {
                            if (!open) {
                                finishEditing();
                            }
                        }}
                        accountFilterPredicate={accountFilterPredicate}
                        hideArrow
                        buttonProps={{
                            variant: "primary",
                            size: "sm",
                            className: triggerClassName,
                            style: {
                                position: "absolute",
                                opacity: 0,
                                pointerEvents: "none",
                            },
                        }}
                    />
                </div>
                {display}
            </>
        );
    }

    return display;
};
